import { snippetCompletion, CompletionContext } from '@codemirror/autocomplete';
import { cssLanguage, css } from '@codemirror/lang-css';
import { html, htmlCompletionSource } from '@codemirror/lang-html';
import { typescriptLanguage, javascriptLanguage, javascript } from '@codemirror/lang-javascript';
import { LRLanguage, indentNodeProp, foldNodeProp, syntaxTree, LanguageSupport } from '@codemirror/language';
import { EditorSelection, EditorState } from '@codemirror/state';
import { parser } from '@glimdown/lezer-glimmer-expression';
import { EditorView } from '@codemirror/view';
import { glimmerParser, configureNesting } from 'lezer-glimmer';

const defaultNesting = [
    {
        tag: 'script',
        attrs: (attrs) => attrs['type'] === 'text/typescript' || attrs['lang'] === 'ts',
        parser: typescriptLanguage.parser,
    },
    {
        tag: 'script',
        attrs(attrs) {
            return (!attrs['type'] ||
                /^(?:text|application)\/(?:x-)?(?:java|ecma)script$|^module$|^$/i.test(attrs['type']));
        },
        parser: javascriptLanguage.parser,
    },
    {
        tag: 'style',
        attrs( /* attrs */) {
            return true;
            // Always use CSS. We can add varying support later
            // return (
            //   (!attrs['lang'] || attrs['lang'] === 'css' || attrs['lang'] === 'scss') &&
            //   (!attrs['type'] || /^(text\/)?(x-)?(stylesheet|css|scss)$/i.test(attrs['type']))
            // );
        },
        parser: cssLanguage.parser,
    },
];
const glimmerPlain = /*@__PURE__*/LRLanguage.define({
    parser: /*@__PURE__*/glimmerParser.configure({
        props: [
            /*@__PURE__*/indentNodeProp.add({
                Element: (context) => {
                    let after = /^(\s*)(<\/)?/.exec(context.textAfter);
                    if (!after)
                        return null;
                    if (context.node.to <= context.pos + after[0].length)
                        return context.continue();
                    return context.lineIndent(context.node.from) + (after[2] ? 0 : context.unit);
                },
                Block: (context) => {
                    const node = context.node;
                    const text = context.textAfter.trim();
                    if (text.startsWith('{/')) {
                        const name = node.name;
                        if ((name === 'IfBlock' && text.startsWith('{/if')) ||
                            (name === 'UnlessBlock' && text.startsWith('{/unless')) ||
                            (name === 'InElementBlock' && text.startsWith('{/in-element')) ||
                            (name === 'EachBlock' && text.startsWith('{/each')) ||
                            (name === 'EachInBlock' && text.startsWith('{/each-in')) ||
                            (name === 'LetBlock' && text.startsWith('{/let'))) {
                            return context.lineIndent(context.node.from);
                        }
                        return null;
                    }
                    if (node.name === 'IfBlock' ||
                        node.name === 'EachBlock' ||
                        node.name === 'EachInBlock' ||
                        node.name === 'UnlessBlock') {
                        if (text.startsWith('{{:else'))
                            return context.lineIndent(node.from);
                        if (text.startsWith('{{else'))
                            return context.lineIndent(node.from);
                    }
                    // not sure if this needed to be duplicated
                    let after = /^(\s*)(<\/)?/.exec(context.textAfter);
                    if (!after)
                        return null;
                    if (context.node.to <= context.pos + after[0].length)
                        return context.continue();
                    return context.lineIndent(context.node.from) + (after[2] ? 0 : context.unit);
                },
                'BlockOpen BlockClose BlockInline': (context) => {
                    return context.column(context.node.from) + context.unit;
                },
                'OpenTag CloseTag SelfClosingTag': (context) => {
                    return context.column(context.node.from) + context.unit;
                },
                Document: (context) => {
                    var _a, _b;
                    if (context.pos + (((_b = (_a = /\s*/.exec(context.textAfter)) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.length) || 0) < context.node.to) {
                        return context.continue();
                    }
                    let endElt = null;
                    let close;
                    for (let cur = context.node;;) {
                        let last = cur.lastChild;
                        if (!last || last.name != 'Element' || last.to != cur.to)
                            break;
                        endElt = cur = last;
                    }
                    if (endElt &&
                        !((close = endElt.lastChild) &&
                            (close.name === 'CloseTag' || close.name === 'SelfClosingTag'))) {
                        return context.lineIndent(endElt.from) + context.unit;
                    }
                    return null;
                },
            }),
            /*@__PURE__*/foldNodeProp.add({
                Block: (node) => {
                    const open = `${node.name}Open`;
                    const close = `${node.name}Close`;
                    const first = node.firstChild;
                    const last = node.lastChild;
                    if (!first || first.name !== open)
                        return null;
                    return { from: first.to, to: (last === null || last === void 0 ? void 0 : last.name) === close ? last.from : node.to };
                },
                Element: (node) => {
                    let first = node.firstChild;
                    let last = node.lastChild;
                    if (!last)
                        return null;
                    if (!first || first.name != 'OpenTag')
                        return null;
                    return { from: first.to, to: last.name === 'CloseTag' ? last.from : node.to };
                },
            }),
        ],
    }),
    languageData: {
        commentTokens: {
            block: { open: '<!--', close: '-->' },
            moustacheLong: { open: '{{!--', close: '--}}' },
        },
        indentOnInput: /^\s*((<\/\w+\W)|(\{\{:?(else|then|catch))|(\{\{\/(if|each|each-in|in-element|unless|let)))$/,
        wordChars: '-._',
    },
});
const glimmerLanguage = /*@__PURE__*/glimmerPlain.configure({
    wrap: /*@__PURE__*/configureNesting(defaultNesting),
});

// unfortunately the HTML language explicitly checks for the language type,
// so we have to duplicate the entire autoCloseTags extension
function elementName(doc, tree, max = doc.length) {
    if (!tree)
        return '';
    let tag = tree.firstChild;
    let name = tag &&
        (tag.getChild('TagName') || tag.getChild('ComponentName') || tag.getChild('SvelteElementName'));
    return name ? doc.sliceString(name.from, Math.min(name.to, max)) : '';
}
const autoCloseTags = /*@__PURE__*/EditorView.inputHandler.of((view, from, to, text) => {
    if (view.composing ||
        view.state.readOnly ||
        from != to ||
        (text != '>' && text != '/') ||
        !glimmerLanguage.isActiveAt(view.state, from, -1))
        return false;
    let { state } = view;
    const noChanges = { range: undefined, changes: undefined, effects: undefined };
    let changes = state.changeByRange((range) => {
        var _a, _b, _c;
        let { head } = range;
        let around = syntaxTree(state).resolveInner(head, -1);
        let name;
        if (around.name === 'TagName' ||
            around.name === 'ComponentName' ||
            around.name === 'StartTag') {
            if (!around.parent)
                return Object.assign(Object.assign({}, noChanges), { range });
            around = around.parent;
        }
        if (text === '>' && around.name === 'OpenTag') {
            if (((_b = (_a = around.parent) === null || _a === void 0 ? void 0 : _a.lastChild) === null || _b === void 0 ? void 0 : _b.name) != 'CloseTag' &&
                (name = elementName(state.doc, around.parent, head))) {
                let hasRightBracket = view.state.doc.sliceString(head, head + 1) === '>';
                let insert = `${hasRightBracket ? '' : '>'}</${name}>`;
                return {
                    range: EditorSelection.cursor(head + 1),
                    changes: { from: head + (hasRightBracket ? 1 : 0), insert },
                };
            }
        }
        else if (text === '/' && around.name === 'OpenTag') {
            let empty = around.parent, base = empty === null || empty === void 0 ? void 0 : empty.parent;
            if (!empty)
                return Object.assign(Object.assign({}, noChanges), { range });
            if (!base)
                return Object.assign(Object.assign({}, noChanges), { range });
            if (empty.from == head - 1 &&
                ((_c = base.lastChild) === null || _c === void 0 ? void 0 : _c.name) != 'CloseTag' &&
                (name = elementName(state.doc, base, head))) {
                let hasRightBracket = view.state.doc.sliceString(head, head + 1) === '>';
                let insert = `/${name}${hasRightBracket ? '' : '>'}`;
                let pos = head + insert.length + (hasRightBracket ? 1 : 0);
                return { range: EditorSelection.cursor(pos), changes: { from: head, insert } };
            }
        }
        return { range };
    });
    if (changes.changes.empty)
        return false;
    view.dispatch(changes, { userEvent: 'input.type', scrollIntoView: true });
    return true;
});

/**
A collection of JavaScript-related
[snippets](https://codemirror.net/6/docs/ref/#autocomplete.snippet).
*/
const snippets = [
    /*@__PURE__*/snippetCompletion('#each ${list} as |${item}|}}\n\t${}\n{{/each', {
        label: '#each',
        detail: 'loop',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#each-in ${object} as |${key} ${value}|}}\n\t${}\n{{/each-in', {
        label: '#each-in',
        detail: 'loop',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#let ${subExpression} as |${result}|}}\n\t${}\n{{/let', {
        label: '#let',
        detail: 'block',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#if ${condition}}}\n\t${}\n{{/if', {
        label: '#if',
        detail: 'block',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#in-element ${element}}}\n\t${}\n{{/in-element', {
        label: '#in-element',
        detail: 'block',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#unless ${condition}}}\n\t${}\n{{/unless', {
        label: '#unless',
        detail: 'block',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#if ${condition}}}\n\t${}\n{{else}}\n\t${}\n{{/if', {
        label: '#if',
        detail: '/ else block',
        type: 'keyword',
    }),
    /*@__PURE__*/snippetCompletion('#unless ${condition}}}\n\t${}\n{{else}}\n\t${}\n{{/unless', {
        label: '#unless',
        detail: '/ else block',
        type: 'keyword',
    }),
];

function glimmer() {
    return new LanguageSupport(glimmerLanguage, [
        javascript().support,
        css().support,
        new LanguageSupport(LRLanguage.define({
            name: 'glimmer-expression',
            parser: parser.configure({}),
        }), []),
        glimmerLanguage.data.of({ autocomplete: snippets }),
        // We may want to swap this with Glimmer-specific tag completion
        glimmerLanguage.data.of({ autocomplete: htmlTagCompletion }),
        autoCloseTags,
    ]);
}
function htmlTagCompletion(context) {
    let { state, pos } = context, m = /<[:\-.\w\u00b7-\uffff]*$/.exec(state.sliceDoc(pos - 25, pos));
    if (!m)
        return null;
    let tree = syntaxTree(state).resolveInner(pos, -1);
    while (tree && !tree.type.isTop) {
        if (tree.name == 'CodeBlock' ||
            tree.name == 'FencedCode' ||
            tree.name == 'ProcessingInstructionBlock' ||
            tree.name == 'CommentBlock' ||
            tree.name == 'Link' ||
            tree.name == 'Image') {
            return null;
        }
        tree = tree.parent;
    }
    return {
        from: pos - m[0].length,
        to: pos,
        options: htmlTagCompletions(),
        validFor: /^<[:\-.\w\u00b7-\uffff]*$/,
    };
}
let _tagCompletions = null;
const htmlNoMatch = /*@__PURE__*/html({ matchClosingTags: false });
function htmlTagCompletions() {
    if (_tagCompletions)
        return _tagCompletions;
    let result = htmlCompletionSource(new CompletionContext(EditorState.create({ extensions: htmlNoMatch }), 0, true));
    return (_tagCompletions = result ? result.options : []);
}

export { defaultNesting, glimmer, glimmerLanguage, glimmerPlain, snippets };
